/**
 * @file Header.
 */

/* eslint-disable max-classes-per-file */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

/**
 * Header component.
 */
const vHeader = () => {
	const header = document.querySelector('.v-header');

	if (!header) {
		return;
	}

	gsap.registerPlugin(ScrollTrigger);

	let SubNavs = [];

	class Subnav {
		constructor(trigger) {
			this.subnavTrigger = trigger;
			this.wrapper = trigger.parentElement;
			this.subnav = this.wrapper.querySelector('.v-header__nav-sublist');
			this.subnavItems = this.subnav.querySelectorAll('.v-header__nav-subitem');
			this.isExpanded = trigger.getAttribute('aria-expanded') === 'true';
			this.isMouseIn = false;
			this.exitTimeout = null;
			this.animation = gsap.timeline({
				defaults:          { ease: 'sine.out' },
				paused:            true,
				onReverseComplete: () => {
					this.subnavTrigger.setAttribute('aria-expanded', 'false');
				},
			})
				.to(this.subnav, {
					opacity:  1,
					y:        0,
					scaleY:   1,
					duration: 0.2,
				})
				.to(this.subnavItems, {
					opacity:  1,
					y:        0,
					duration: 0.2,
					stagger:  0.075,
				});

			this.init();
		}

		openSubnav() {
			this.isExpanded = true;
			this.subnavTrigger.setAttribute('aria-expanded', 'true');
			this.animation.timeScale(1).play();
			SubNavs.filter((nav) => nav !== this).forEach((nav) => nav.closeSubnav());
		}

		closeSubnav() {
			this.isExpanded = false;
			this.animation.timeScale(1.5).reverse();
		}

		toggleSubnav() {
			if (this.isExpanded) {
				if (!this.isMouseIn) this.closeSubnav();
			} else {
				this.openSubnav();
			}
		}

		init() {
			this.subnavTrigger.addEventListener('click', () => {
				this.toggleSubnav();
			});

			this.wrapper.addEventListener('mouseenter', () => {
				this.openSubnav();
				this.isMouseIn = true;
				if (this.exitTimeout) clearTimeout(this.exitTimeout);
			});

			this.wrapper.addEventListener('mouseleave', () => {
				this.isMouseIn = false;
				this.exitTimeout = setTimeout(() => {
					if (!this.isMouseIn) {
						this.closeSubnav();
					}
				}, 400);
			});
		}
	}

	const subnavTriggers = document.querySelectorAll('.v-header__nav-link--has-children');
	SubNavs = [...subnavTriggers].map((trigger) => new Subnav(trigger));

	/* MOBILE */
	let isMobileNavAnimating = false;
	let MobileSubNavs = [];
	const mobileNavTrigger = document.querySelector('.v-header__nav-trigger');
	const mobileNavClose = document.querySelector('.v-header__mobile-nav-close');
	const mobileNav = document.querySelector('.v-header__mobile-nav');
	const mobileWrapper = document.querySelector('.v-header__mobile-nav-wrapper');
	const mobileWrapperOverlay = mobileWrapper.querySelector('.v-header__mobile-nav-wrapper-overlay');
	const mobileNavItems = document.querySelectorAll('.v-header__mobile-nav-item--top-level');
	const mobileNavLinks = document.querySelectorAll('.v-header__mobile-nav-link--top-level');

	const mobileNavAnimation = gsap.timeline({
		paused:   true,
		defaults: { ease: 'sine.out' },
		onStart:  () => {
			mobileNav.classList.add('v-header__mobile-nav--open');
		},
		onComplete: () => {
			[...mobileNavItems].forEach((item) => { item.removeAttribute('style'); });
			isMobileNavAnimating = false;
		},
		onReverseComplete: () => {
			mobileNav.classList.remove('v-header__mobile-nav--open');
			isMobileNavAnimating = false;
		},
	});
	mobileNavAnimation
		.from(mobileNav, {
			opacity: 0,
		})
		.from(mobileWrapper, {
			opacity: 0,
			scaleX:  0,
		})
		.from(mobileNavClose, {
			opacity: 0,
		}, '<')
		.from(mobileNavItems, {
			opacity:  0,
			yPercent: -25,
			stagger:  0.1,
		})
		.from(mobileNavLinks, {
			opacity:  0,
			yPercent: -12.5,
			stagger:  0.1,
		}, '<+.2');

	mobileNavTrigger.addEventListener('click', () => {
		if (isMobileNavAnimating) return;
		isMobileNavAnimating = true;
		mobileNavAnimation.timeScale(1).restart();
	});
	mobileNavClose.addEventListener('click', () => {
		if (isMobileNavAnimating) return;
		const openSub = MobileSubNavs.filter((nav) => nav.isExpanded);
		if (openSub.length) {
			openSub[0].closeSubnav(() => {
				isMobileNavAnimating = true;
				mobileNavAnimation.timeScale(1.5).reverse();
			});
		} else {
			isMobileNavAnimating = true;
			mobileNavAnimation.timeScale(1.5).reverse();
		}
	});
	mobileWrapperOverlay.addEventListener('click', () => {
		if (isMobileNavAnimating) return;
		const openSub = MobileSubNavs.filter((nav) => nav.isExpanded);
		if (openSub.length) {
			openSub[0].closeSubnav();
		}
	});

	class MobileSubnav {
		constructor(trigger) {
			this.subnav = trigger.parentElement.querySelector('.v-header__mobile-nav-subwrapper');
			this.subnavTrigger = trigger;
			this.backTrigger = this.subnav.querySelector('.v-header__mobile-nav-back');
			this.subnavItems = this.subnav.querySelectorAll('.v-header__mobile-nav-item');
			this.subnavLinks = this.subnav.querySelectorAll('.v-header__mobile-nav-link');
			this.isExpanded = this.subnavTrigger.getAttribute('aria-expanded') === 'true';
			this.closeCallback = null;
			this.animation = gsap.timeline({
				paused:   true,
				defaults: { ease: 'sine.out', duration: 0.4 },
				onStart:  () => {
					mobileWrapper.classList.add('v-header__mobile-nav-wrapper--behind');
					this.subnav.classList.add('v-header__mobile-nav-subwrapper--open');
				},
				onComplete: () => {
					isMobileNavAnimating = false;
				},
				onReverseComplete: () => {
					isMobileNavAnimating = false;
					mobileWrapper.classList.remove('v-header__mobile-nav-wrapper--behind');
					this.subnav.classList.remove('v-header__mobile-nav-subwrapper--open');
					if (this.closeCallback && typeof this.closeCallback === 'function') {
						this.closeCallback();
						this.closeCallback = null;
					}
				},
			})
				.to(mobileWrapperOverlay, {
					opacity: 1,
				})
				.to(mobileWrapper, {
					scaleY:   0.9,
					onUpdate: () => {
						this.subnav.style.transform = `scaleY(${1 / gsap.getProperty(mobileWrapper, 'scaleY')})`;
					},
				}, '<')
				.to(mobileNavLinks, {
					scaleX: 0.9,
				}, '<')
				.to(this.subnav, {
					'--clip': '0%',
				}, '<+50%')
				.from(this.backTrigger, {
					opacity: 0,
				}, '>')
				.from(this.subnavItems, {
					opacity:  0,
					yPercent: -25,
					stagger:  0.1,
				}, '<')
				.from(this.subnavLinks, {
					opacity:  0,
					yPercent: -12.5,
					stagger:  0.1,
				}, '<+.2');

			this.init();
		}

		openSubnav() {
			if (isMobileNavAnimating) return;
			isMobileNavAnimating = true;
			this.isExpanded = true;
			this.subnavTrigger.getAttribute('aria-expanded', 'true');
			this.animation.timeScale(1).restart();
		}

		closeSubnav(cb) {
			if (isMobileNavAnimating) return;
			isMobileNavAnimating = true;
			this.isExpanded = false;
			this.subnavTrigger.getAttribute('aria-expanded', 'false');
			if (cb && typeof cb === 'function') {
				this.closeCallback = cb;
			}
			this.animation.timeScale(1.5).reverse();
		}

		init() {
			this.subnavTrigger.addEventListener('click', () => this.openSubnav());
			this.backTrigger.addEventListener('click', () => this.closeSubnav());
		}
	}

	const mobileSubnavTriggers = document.querySelectorAll('.v-header__mobile-nav-link--has-children');
	MobileSubNavs = [...mobileSubnavTriggers].map((trigger) => new MobileSubnav(trigger));

	let mobileNavTriggerAnimation;

	ScrollTrigger.create({
		trigger: 'body',
		start:   '200px top',
		end:     '+=1',
		onEnter: () => {
			if (mobileNavTriggerAnimation) mobileNavTriggerAnimation.kill();
			mobileNavTriggerAnimation = gsap.to(mobileNavTrigger, {
				opacity:  1,
				ease:     'sine.out',
				duration: 0.4,
				onStart:  () => {
					gsap.set(mobileNavTrigger, {
						display: 'block',
					});
				},
				onComplete: () => {
					mobileNavTrigger.removeAttribute('style');
					mobileNavTrigger.classList.add('v-header__nav-trigger--stuck');
				},
			});
		},
		onLeaveBack: () => {
			if (mobileNavTriggerAnimation) mobileNavTriggerAnimation.kill();
			mobileNavTriggerAnimation = gsap.to(mobileNavTrigger, {
				opacity:    0,
				ease:       'sine.out',
				duration:   0.4,
				onComplete: () => {
					mobileNavTrigger.removeAttribute('style');
					mobileNavTrigger.classList.remove('v-header__nav-trigger--stuck');
				},
			});
		},
	});
};

export default vHeader;
